import { useEffect, useMemo, useState } from "react";
import axios from "axios";

/* ==============================
   CONFIG
============================== */
const BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  "https://bmtvendorvendorfinal.onrender.com";
const APT_API = `${BASE}/api/vendor/apartment`;
const INV_API = `${BASE}/api/apartment-inventory`;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PAGE_SIZE = 25;
const EMPTY_FORM = { totalUnits: "", blockedUnits: "", price: "", resetPrice: false, minNights: "", stopSell: "" };

/* ==============================
   HELPERS
============================== */
const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const dateToStr = (t) => ymd(t.getFullYear(), t.getMonth(), t.getDate());
const todayStr = () => dateToStr(new Date());
const addDaysStr = (s, n) => {
  const d = new Date(`${s}T00:00:00`);
  d.setDate(d.getDate() + n);
  return dateToStr(d);
};
const fmtDate = (ds) =>
  new Date(`${ds}T00:00:00Z`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
const inr = (n) =>
  n === undefined || n === null || n === ""
    ? "-"
    : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n));
const dayDiff = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000);
const availOf = (r) => Math.max(0, r.totalUnits - r.bookedUnits - r.blockedUnits);
const isInactive = (a) => a?.status === "inactive";
const rowDate = (r) => String(r.date).slice(0, 10);
const rowToForm = (r) => ({
  totalUnits: String(r.totalUnits),
  blockedUnits: String(r.blockedUnits),
  price: r.price != null ? String(r.price) : "",
  resetPrice: false,
  minNights: String(r.minNights),
  stopSell: r.stopSell ? "stop" : "",
});

/* ==============================
   MAIN COMPONENT
============================== */
export default function VendorApartmentInventory() {
  const token = localStorage.getItem("vendorToken") || localStorage.getItem("token");
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const today = todayStr();

  // common
  const [screen, setScreen] = useState("list"); // "list" | "form"
  const [apartments, setApartments] = useState([]);
  const [aptId, setAptId] = useState(new URLSearchParams(window.location.search).get("apartment") || "");
  const [aptInfo, setAptInfo] = useState(null);
  const [loadingApts, setLoadingApts] = useState(true);
  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState(null);

  // list (table)
  const [listRange, setListRange] = useState({ from: today, to: addDaysStr(today, 60) });
  const [tableRows, setTableRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [page, setPage] = useState(1);
  const [onlyStatus, setOnlyStatus] = useState("");
  const [deleteDate, setDeleteDate] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // form (full page)
  const [editing, setEditing] = useState(false);
  const [month, setMonth] = useState(() => ({ y: new Date().getFullYear(), m: new Date().getMonth() }));
  const [calRows, setCalRows] = useState({});
  const [loadingCal, setLoadingCal] = useState(false);
  const [range, setRange] = useState({ start: "", end: "" });
  const [weekdays, setWeekdays] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  /* ---------- apartments ---------- */
  useEffect(() => {
    if (!token) {
      setLoadingApts(false);
      setPageError("Session not found. Please login again.");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(APT_API, authHeaders);
        const list = res.data.data || [];
        setApartments(list);
        setAptId((cur) => (list.some((a) => a._id === cur) ? cur : list[0]?._id || ""));
      } catch (e) {
        setPageError(
          e.response?.status === 401
            ? "Session expired. Please login again."
            : e.response?.data?.message || "Failed to load apartments."
        );
      } finally {
        setLoadingApts(false);
      }
    })();
  }, [token, authHeaders]);

  /* ---------- table data ---------- */
  const rangeValid = listRange.from && listRange.to && listRange.to >= listRange.from && dayDiff(listRange.from, listRange.to) + 1 <= 366;

  useEffect(() => {
    if (!aptId || !rangeValid) return;
    let cancelled = false;
    (async () => {
      setLoadingList(true);
      setPageError("");
      try {
        const res = await axios.get(`${INV_API}/${aptId}`, { ...authHeaders, params: { from: listRange.from, to: listRange.to } });
        if (cancelled) return;
        setTableRows(res.data.data || []);
        setAptInfo(res.data.apartment || null);
      } catch (e) {
        if (!cancelled) setPageError(e.response?.data?.message || "Failed to load inventory.");
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [aptId, listRange, rangeValid, reloadKey, authHeaders]);

  useEffect(() => { setPage(1); setDeleteDate(null); }, [aptId, listRange, onlyStatus]);

  const basePrice = aptInfo?.basePrice;

  const statusOf = (r) => (r.stopSell ? "stopped" : availOf(r) === 0 ? "sold" : "open");

  const shownRows = useMemo(
    () => (onlyStatus ? tableRows.filter((r) => statusOf(r) === onlyStatus) : tableRows),
    [tableRows, onlyStatus]
  );
  const pages = Math.max(1, Math.ceil(shownRows.length / PAGE_SIZE));
  const pageRows = shownRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = useMemo(
    () => ({
      set: tableRows.length,
      booked: tableRows.reduce((s, r) => s + r.bookedUnits, 0),
      soldOut: tableRows.filter((r) => statusOf(r) === "sold").length,
      stopped: tableRows.filter((r) => r.stopSell).length,
    }),
    [tableRows]
  );

  /* ---------- calendar data (form page only) ---------- */
  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
  const firstDow = new Date(month.y, month.m, 1).getDay();

  useEffect(() => {
    if (screen !== "form" || !aptId) return;
    let cancelled = false;
    (async () => {
      setLoadingCal(true);
      try {
        const res = await axios.get(`${INV_API}/${aptId}`, {
          ...authHeaders,
          params: { from: ymd(month.y, month.m, 1), to: ymd(month.y, month.m, daysInMonth) },
        });
        if (cancelled) return;
        const map = {};
        (res.data.data || []).forEach((r) => { map[rowDate(r)] = r; });
        setCalRows(map);
      } catch {
        if (!cancelled) setCalRows({});
      } finally {
        if (!cancelled) setLoadingCal(false);
      }
    })();
    return () => { cancelled = true; };
  }, [screen, aptId, month, daysInMonth, authHeaders]);

  /* ---------- open / close form page ---------- */
  useEffect(() => { window.scrollTo({ top: 0 }); }, [screen]);

  const openAdd = () => {
    const n = new Date();
    setEditing(false);
    setRange({ start: "", end: "" });
    setForm(EMPTY_FORM);
    setWeekdays([]);
    setFormError("");
    setMonth({ y: n.getFullYear(), m: n.getMonth() });
    setScreen("form");
  };

  const openEdit = (r) => {
    const ds = rowDate(r);
    setEditing(true);
    setRange({ start: ds, end: "" });
    setForm(rowToForm(r));
    setWeekdays([]);
    setFormError("");
    setMonth({ y: Number(ds.slice(0, 4)), m: Number(ds.slice(5, 7)) - 1 });
    setScreen("form");
  };

  const closeForm = () => {
    if (saving) return;
    setScreen("list");
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && screen === "form" && !saving) setScreen("list"); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [screen, saving]);

  /* ---------- calendar interaction ---------- */
  const shiftMonth = (d) =>
    setMonth(({ y, m }) => {
      const n = new Date(y, m + d, 1);
      return { y: n.getFullYear(), m: n.getMonth() };
    });
  const goToday = () => { const n = new Date(); setMonth({ y: n.getFullYear(), m: n.getMonth() }); };

  const onDayClick = (ds) => {
    if (ds < today) return;
    setFormError("");
    if (!range.start || range.end) {
      setRange({ start: ds, end: "" });
      const r = calRows[ds];
      setForm(r ? rowToForm(r) : EMPTY_FORM);
      return;
    }
    if (ds < range.start) setRange({ start: ds, end: range.start });
    else setRange({ start: range.start, end: ds });
  };

  const endDate = range.end || range.start;
  const inRange = (ds) => !!range.start && ds >= range.start && ds <= endDate;
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleWeekday = (n) => setWeekdays((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));
  const pickMonth = () =>
    setRange({
      start: ymd(month.y, month.m, 1) < today ? today : ymd(month.y, month.m, 1),
      end: ymd(month.y, month.m, daysInMonth),
    });

  /* ---------- save ---------- */
  const validateForm = () => {
    if (!range.start) return "Calendar se date chuno ya start date bharo.";
    if (endDate < range.start) return "End date, start date se pehle nahi ho sakti.";
    if (dayDiff(range.start, endDate) + 1 > 366) return "Ek baar me max 366 din select kar sakte ho.";
    const hasField = form.totalUnits !== "" || form.blockedUnits !== "" || form.price !== "" || form.resetPrice || form.minNights !== "" || form.stopSell !== "";
    if (!hasField) return "Kam se kam ek cheez bharo (units, price, min nights ya stop sell).";
    for (const k of ["totalUnits", "blockedUnits", "minNights"]) {
      if (form[k] !== "" && (!Number.isInteger(Number(form[k])) || Number(form[k]) < (k === "minNights" ? 1 : 0)))
        return k === "minNights" ? "Min nights kam se kam 1 hona chahiye." : "Units poore number (0 ya zyada) hone chahiye.";
    }
    if (form.price !== "" && Number(form.price) < 0) return "Price negative nahi ho sakta.";
    if (form.totalUnits !== "" && form.blockedUnits !== "" && Number(form.blockedUnits) > Number(form.totalUnits))
      return "Blocked units, total units se zyada nahi ho sakte.";
    return "";
  };

  const save = async (e) => {
    e.preventDefault();
    const problem = validateForm();
    if (problem) return setFormError(problem);

    const body = { startDate: range.start, endDate };
    if (weekdays.length) body.weekdays = weekdays;
    if (form.totalUnits !== "") body.totalUnits = Number(form.totalUnits);
    if (form.blockedUnits !== "") body.blockedUnits = Number(form.blockedUnits);
    if (form.resetPrice) body.price = null;
    else if (form.price !== "") body.price = Number(form.price);
    if (form.minNights !== "") body.minNights = Number(form.minNights);
    if (form.stopSell) body.stopSell = form.stopSell === "stop";

    setSaving(true);
    setFormError("");
    try {
      const res = await axios.post(`${INV_API}/${aptId}/bulk`, body, authHeaders);
      // saved dates table me dikhen, isliye table ki range badha do
      let from = listRange.from < range.start ? listRange.from : range.start;
      let to = listRange.to > endDate ? listRange.to : endDate;
      if (dayDiff(from, to) + 1 > 366) to = addDaysStr(from, 365);
      setListRange({ from, to });
      setReloadKey((k) => k + 1);
      setScreen("list");
      showToast(res.data.message || "Inventory saved.");
    } catch (err) {
      const d = err.response?.data;
      setFormError(
        d?.dates?.length
          ? `${d.message} (${d.dates.slice(0, 5).join(", ")}${d.dates.length > 5 ? "..." : ""})`
          : d?.message || "Inventory save nahi ho payi. Dobara try karo."
      );
    } finally {
      setSaving(false);
    }
  };

  /* ---------- delete a row ---------- */
  const confirmDelete = async (ds) => {
    setDeleting(true);
    try {
      await axios.delete(`${INV_API}/${aptId}`, { ...authHeaders, params: { from: ds, to: ds } });
      setTableRows((p) => p.filter((r) => rowDate(r) !== ds));
      setDeleteDate(null);
      showToast("Inventory row delete ho gayi.");
    } catch (err) {
      showToast(err.response?.data?.message || "Delete nahi ho paya.", "err");
    } finally {
      setDeleting(false);
    }
  };

  /* ---------- calendar cells ---------- */
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(<div key={`b${i}`} className="vi-cell blank" />);
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = ymd(month.y, month.m, d);
    const r = calRows[ds];
    const past = ds < today;
    const avail = r ? availOf(r) : null;
    let state = "empty";
    if (r) state = r.stopSell ? "stop" : avail === 0 ? "sold" : "open";
    cells.push(
      <button
        key={ds}
        type="button"
        disabled={past}
        onClick={() => onDayClick(ds)}
        className={`vi-cell ${state} ${past ? "past" : ""} ${inRange(ds) ? "sel" : ""} ${range.start === ds || (range.start && endDate === ds) ? "edge" : ""} ${ds === today ? "today" : ""}`}
        aria-pressed={inRange(ds)}
        aria-label={`${d} ${MONTHS[month.m]}`}
      >
        <span className="vi-day">{d}</span>
        {r ? (
          <>
            <span className="vi-units">{r.stopSell ? "Stopped" : `${avail}/${r.totalUnits} free`}</span>
            <span className="vi-price">{inr(r.price ?? basePrice)}</span>
          </>
        ) : (
          <span className="vi-units muted">Not set</span>
        )}
      </button>
    );
  }

  const aptName = apartments.find((a) => a._id === aptId)?.apartmentName || "";

  /* ==============================
     JSX
  ============================== */
  return (
    <div className="vi">
      <style>{css}</style>

      {/* ============ LIST (DATA TABLE) ============ */}
      {screen === "list" && (
        <>
          <header className="vi-head">
            <div>
              <h1>Apartment inventory</h1>
              <p>Date-wise units aur price. Booking aane par units apne aap kam honge.</p>
            </div>
            {apartments.length > 0 && (
              <button type="button" className="btn vi-add" onClick={openAdd}>
                + Add inventory
              </button>
            )}
          </header>

          {loadingApts ? (
            <div className="vi-state"><div className="spinner" /></div>
          ) : apartments.length === 0 ? (
            <div className="vi-state">
              <p>{pageError || "Abhi koi apartment nahi hai. Pehle \"Manage Apartments\" se apartment add karo."}</p>
            </div>
          ) : (
            <>
              <section className="vi-card toolbar">
                <div className="field grow">
                  <label htmlFor="vi-apt">Apartment</label>
                  <select id="vi-apt" value={aptId} onChange={(e) => setAptId(e.target.value)}>
                    {apartments.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.apartmentName}{a.city ? ` - ${a.city}` : ""}{isInactive(a) ? " (inactive)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="vi-from">From</label>
                  <input id="vi-from" type="date" value={listRange.from} onChange={(e) => setListRange((p) => ({ ...p, from: e.target.value }))} />
                </div>
                <div className="field">
                  <label htmlFor="vi-to">To</label>
                  <input id="vi-to" type="date" min={listRange.from} value={listRange.to} onChange={(e) => setListRange((p) => ({ ...p, to: e.target.value }))} />
                </div>
                <div className="field">
                  <label htmlFor="vi-st">Status</label>
                  <select id="vi-st" value={onlyStatus} onChange={(e) => setOnlyStatus(e.target.value)}>
                    <option value="">All</option>
                    <option value="open">Open</option>
                    <option value="sold">Sold out</option>
                    <option value="stopped">Stopped</option>
                  </select>
                </div>
              </section>

              {isInactive(aptInfo) && (
                <div className="vi-notice" role="status">
                  Ye apartment abhi inactive hai, isliye guests ko nahi dikhegi. Inventory phir bhi save hogi.
                </div>
              )}
              {!rangeValid && <div className="vi-error" role="alert">Date range sahi chuno (max 366 din, To &ge; From).</div>}
              {pageError && <div className="vi-error" role="alert">{pageError} <button className="btn sm" onClick={() => setReloadKey((k) => k + 1)}>Try again</button></div>}

              <section className="vi-stats">
                <div><span>{stats.set}</span>days in table</div>
                <div><span>{stats.booked}</span>units booked</div>
                <div><span>{stats.soldOut}</span>sold-out days</div>
                <div><span>{stats.stopped}</span>stopped days</div>
                <div><span>{aptInfo?.totalUnits ?? "-"}</span>default units</div>
                <div><span>{inr(basePrice)}</span>base price</div>
              </section>

              {loadingList ? (
                <div className="vi-state"><div className="spinner" /></div>
              ) : shownRows.length === 0 ? (
                <div className="vi-state">
                  <div className="empty-ico">▦</div>
                  <p>{tableRows.length === 0 ? `${aptName || "Is apartment"} ki is range me koi inventory set nahi hai.` : "Is filter me koi row nahi mili."}</p>
                  {tableRows.length === 0 && <button type="button" className="btn vi-main" onClick={openAdd}>Add inventory</button>}
                </div>
              ) : (
                <div className="vi-card table-card">
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="num">Total</th>
                          <th className="num">Booked</th>
                          <th className="num">Blocked</th>
                          <th className="num">Available</th>
                          <th className="num">Price / night</th>
                          <th className="num">Min nights</th>
                          <th>Status</th>
                          <th className="right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((r) => {
                          const ds = rowDate(r);
                          const st = statusOf(r);
                          const past = ds < today;
                          const asking = deleteDate === ds;
                          return (
                            <tr key={r._id || ds} className={past ? "past-row" : ""}>
                              <td className="date">{fmtDate(ds)}{ds === today && <em className="today-tag">Today</em>}</td>
                              <td className="num">{r.totalUnits}</td>
                              <td className="num">{r.bookedUnits}</td>
                              <td className="num">{r.blockedUnits}</td>
                              <td className="num"><strong>{availOf(r)}</strong></td>
                              <td className="num">{r.price != null ? inr(r.price) : <span className="muted">{inr(basePrice)} <small>base</small></span>}</td>
                              <td className="num">{r.minNights}</td>
                              <td><span className={`badge ${st}`}>{st === "open" ? "Open" : st === "sold" ? "Sold out" : "Stopped"}</span></td>
                              <td className="right">
                                {asking ? (
                                  <span className="confirm">
                                    Delete?
                                    <button className="btn danger-solid sm" disabled={deleting} onClick={() => confirmDelete(ds)}>{deleting ? "..." : "Yes"}</button>
                                    <button className="btn sm" disabled={deleting} onClick={() => setDeleteDate(null)}>No</button>
                                  </span>
                                ) : (
                                  <span className="row-actions">
                                    <button className="btn sm" disabled={past} title={past ? "Beeti hui date edit nahi hoti" : "Edit"} onClick={() => openEdit(r)}>Edit</button>
                                    <button className="btn sm danger" disabled={r.bookedUnits > 0} title={r.bookedUnits > 0 ? "Booking wali date delete nahi hoti" : "Delete"} onClick={() => setDeleteDate(ds)}>Delete</button>
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="pager">
                    <span>
                      Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, shownRows.length)} of {shownRows.length}
                    </span>
                    <div>
                      <button className="btn sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                      <span className="pg">Page {page} / {pages}</span>
                      <button className="btn sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ============ ADD / EDIT (FULL PAGE) ============ */}
      {screen === "form" && (
        <div className="page-wrap">
          <button type="button" className="back" onClick={closeForm}>&larr; Back to inventory</button>

          <div className="vi-card">
            <h1 className="form-title">{editing ? "Edit inventory" : "Add inventory"}</h1>
            <p className="hint">{aptName}{isInactive(aptInfo) ? " (inactive)" : ""}</p>
          </div>

          <section className="vi-card">
            <div className="cal-top">
              <div className="monthnav">
                <button type="button" className="btn" onClick={() => shiftMonth(-1)} aria-label="Previous month">&lsaquo;</button>
                <strong>{MONTHS[month.m]} {month.y}</strong>
                <button type="button" className="btn" onClick={() => shiftMonth(1)} aria-label="Next month">&rsaquo;</button>
                <button type="button" className="btn soft" onClick={goToday}>Today</button>
              </div>
              <div className="legend">
                <i className="lg open" />Available
                <i className="lg sold" />Sold out
                <i className="lg stop" />Stopped
                <i className="lg empty" />Not set
              </div>
            </div>
            <p className="hint">Ek din ke liye us din par click karo. Range ke liye pehle start din, phir end din par click karo.</p>
            <div className={`vi-cal ${loadingCal ? "loading" : ""}`}>
              {DOW.map((d) => <div key={d} className="vi-dow">{d}</div>)}
              {cells}
            </div>
            <div className="cal-actions">
              <button type="button" className="btn sm" onClick={pickMonth}>Select whole month</button>
              <button type="button" className="btn sm" onClick={() => setRange({ start: "", end: "" })} disabled={!range.start}>Clear selection</button>
            </div>
          </section>

          <form className="vi-card form-card" onSubmit={save} noValidate>
            <h2>Details</h2>
            <p className="hint">Sirf wahi cheezein bharo jo set ya badalni hain. Khaali chhodi hui cheez waise hi rahegi.</p>

            {formError && <div className="vi-error" role="alert">{formError}</div>}

            <div className="vi-grid">
              <div className="field">
                <label htmlFor="vi-start">Start date</label>
                <input id="vi-start" type="date" min={today} value={range.start} onChange={(e) => setRange((p) => ({ ...p, start: e.target.value }))} />
              </div>
              <div className="field">
                <label htmlFor="vi-end">End date (khaali = sirf start din)</label>
                <input id="vi-end" type="date" min={range.start || today} value={range.end} onChange={(e) => setRange((p) => ({ ...p, end: e.target.value }))} />
              </div>
            </div>

            <div className="field">
              <label>Sirf in din par lagao (optional)</label>
              <div className="chips">
                {DOW.map((d, i) => (
                  <button key={d} type="button" aria-pressed={weekdays.includes(i)} className={`chip ${weekdays.includes(i) ? "on" : ""}`} onClick={() => toggleWeekday(i)}>{d}</button>
                ))}
                <button type="button" className="chip" onClick={() => setWeekdays([5, 6])}>Fri + Sat</button>
                <button type="button" className="chip" onClick={() => setWeekdays([])}>All days</button>
              </div>
            </div>

            <div className="vi-grid four">
              <div className="field">
                <label htmlFor="vi-total">Total units</label>
                <input id="vi-total" type="number" min="0" value={form.totalUnits} placeholder="e.g., 3" onChange={(e) => setF("totalUnits", e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="vi-blocked">Blocked units</label>
                <input id="vi-blocked" type="number" min="0" value={form.blockedUnits} placeholder="e.g., 1" onChange={(e) => setF("blockedUnits", e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="vi-price">Price per night (INR)</label>
                <input id="vi-price" type="number" min="0" value={form.price} disabled={form.resetPrice} placeholder={basePrice ? `Base ${basePrice}` : "e.g., 2800"} onChange={(e) => setF("price", e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="vi-min">Min nights</label>
                <input id="vi-min" type="number" min="1" value={form.minNights} placeholder="e.g., 2" onChange={(e) => setF("minNights", e.target.value)} />
              </div>
            </div>

            <div className="vi-grid">
              <div className="field">
                <label htmlFor="vi-stop">Selling status</label>
                <select id="vi-stop" value={form.stopSell} onChange={(e) => setF("stopSell", e.target.value)}>
                  <option value="">No change</option>
                  <option value="stop">Stop sell (bookings band)</option>
                  <option value="open">Open sell (bookings chalu)</option>
                </select>
              </div>
              <label className={`check ${form.resetPrice ? "on" : ""}`}>
                <input type="checkbox" checked={form.resetPrice} onChange={(e) => setF("resetPrice", e.target.checked)} />
                Price ko apartment ke base price par wapas lao
              </label>
            </div>

            <div className="vi-foot">
              <button type="button" className="btn" onClick={closeForm} disabled={saving}>Cancel</button>
              <button type="submit" className="btn vi-main" disabled={saving}>
                {saving ? "Saving..." : range.start && endDate !== range.start ? `Save ${dayDiff(range.start, endDate) + 1} days` : "Save inventory"}
              </button>
            </div>
          </form>
        </div>
      )}

      {toast && <div className={`toast ${toast.type}`} role="status">{toast.msg}</div>}
    </div>
  );
}

/* ==============================
   STYLES
============================== */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Outfit:wght@400;500;600&display=swap');
.vi{--ink:#0f172a;--muted:#64748b;--line:#e2e8f0;--bg:#f5faff;--accent:#0369a1;--accent-d:#075985;--soft:#e0f2fe;--danger:#dc2626;--green:#15803d;
  font-family:'Outfit',system-ui,sans-serif;color:var(--ink);background:var(--bg);min-height:100vh;max-width:1200px;margin:0 auto;padding:32px 24px}
.vi *{box-sizing:border-box}
.vi h1,.vi h2{font-family:'Syne',sans-serif;margin:0}
.vi-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;margin-bottom:20px}
.vi-head h1,.form-title{font-size:30px;color:#0c4a6e}
.vi-head p{margin:6px 0 0;color:var(--muted);font-size:14px}
.btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;white-space:nowrap;flex-shrink:0;height:auto;min-height:0;line-height:1.2;border:1px solid var(--line);background:#fff;color:var(--ink);padding:10px 18px;border-radius:8px;font:600 14px 'Outfit',sans-serif;cursor:pointer}
.btn:hover:not(:disabled){border-color:#b6c4d3}
.btn.sm{padding:6px 12px;font-size:13px}
.btn.soft{background:var(--soft);border-color:#bae6fd;color:var(--accent)}
.btn.danger{color:var(--danger);border-color:#f3c4c4}
.btn.danger-solid{background:var(--danger);border-color:var(--danger);color:#fff}
.btn:disabled{opacity:.45;cursor:not-allowed}

/* primary buttons (unique class names so global CSS cannot break them) */
.vi .btn.vi-main,.vi .btn.vi-add{background:var(--accent);border-color:var(--accent);color:#fff;width:auto!important;height:auto!important;min-width:0;min-height:44px!important;overflow:visible!important;position:static!important;white-space:nowrap!important;text-indent:0;letter-spacing:normal}
.vi .btn.vi-main:hover:not(:disabled),.vi .btn.vi-add:hover:not(:disabled){background:var(--accent-d);border-color:var(--accent-d)}
.vi .btn.vi-add{padding:12px 24px;font-size:15px;min-width:170px}

.vi button:focus-visible,.vi input:focus-visible,.vi select:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.vi-card{background:#fff;border-radius:16px;box-shadow:0 2px 14px rgba(15,23,42,.07);padding:22px 24px;margin-bottom:20px}
.vi-card h2{font-size:20px}
.toolbar{display:flex;gap:16px;align-items:flex-end;flex-wrap:wrap}
.field{display:flex;flex-direction:column;gap:6px;margin-bottom:14px}
.toolbar .field{margin-bottom:0}
.field.grow{flex:1;min-width:240px}
.field label{font-size:13px;font-weight:600;color:#334155}
.vi input[type=number],.vi input[type=date],.vi select{width:100%;padding:10px 13px;border:1.5px solid var(--line);border-radius:8px;font:14px 'Outfit',sans-serif;background:#fff;color:var(--ink)}
.vi input:disabled{background:#f1f5f9}
.vi input:focus,.vi select:focus{border-color:var(--accent);box-shadow:0 0 0 3px rgba(3,105,161,.12)}
.vi-notice{background:#fef3c7;color:#92400e;border:1px solid #fde68a;border-radius:10px;padding:11px 15px;font-size:14px;margin-bottom:20px}
.vi-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:20px}
.vi-stats div{background:#fff;border-radius:12px;padding:14px 16px;font-size:12px;color:var(--muted);box-shadow:0 1px 4px rgba(15,23,42,.06)}
.vi-stats span{display:block;font:700 20px 'Syne',sans-serif;color:#0c4a6e;margin-bottom:2px}

/* table */
.table-card{padding:0;overflow:hidden}
.table-scroll{overflow-x:auto}
.vi table{width:100%;border-collapse:collapse;min-width:900px}
.vi th{padding:14px 16px;text-align:left;font-size:12px;font-weight:600;color:var(--accent);background:#f0f9ff;border-bottom:1px solid var(--soft);white-space:nowrap}
.vi td{padding:13px 16px;font-size:14px;border-bottom:1px solid #f0f6fb;vertical-align:middle}
.vi tbody tr:hover{background:#f8fcff}
.vi tbody tr:last-child td{border-bottom:0}
.vi .num{text-align:right}
.vi .right{text-align:right}
.vi td.date{font-weight:600;white-space:nowrap}
.today-tag{font-style:normal;font-size:11px;font-weight:600;background:var(--soft);color:var(--accent-d);padding:2px 8px;border-radius:999px;margin-left:8px}
.past-row td{color:#94a3b8}
.muted{color:var(--muted)}
.vi td small{font-size:11px;color:var(--muted)}
.badge{font-size:12px;font-weight:600;padding:3px 11px;border-radius:999px;display:inline-block}
.badge.open{background:#dcfce7;color:#166534}
.badge.sold{background:#fee2e2;color:#b91c1c}
.badge.stopped{background:#e2e8f0;color:#475569}
.row-actions{display:inline-flex;gap:6px}
.confirm{display:inline-flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:13px;color:#b91c1c;font-weight:600}
.pager{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;padding:14px 18px;border-top:1px solid var(--soft);font-size:13px;color:var(--muted)}
.pager>div{display:flex;align-items:center;gap:10px}
.pg{font-weight:600;color:var(--ink)}

/* form page */
.page-wrap{max-width:980px;margin:0 auto}
.back{background:none;border:0;padding:6px 0;margin-bottom:12px;font:600 14px 'Outfit',sans-serif;color:var(--accent);cursor:pointer}
.back:hover{text-decoration:underline}
.cal-top{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px}
.monthnav{display:flex;align-items:center;gap:10px}
.monthnav strong{min-width:150px;text-align:center;font-family:'Syne',sans-serif;font-size:17px}
.legend{display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;font-size:12px;color:var(--muted)}
.lg{display:inline-block;width:12px;height:12px;border-radius:3px;margin-right:4px;vertical-align:-1px;border:1px solid #cbd5e1}
.lg.open{background:#dcfce7;border-color:#86efac}
.lg.sold{background:#fee2e2;border-color:#fca5a5}
.lg.stop{background:#e2e8f0;border-color:#94a3b8}
.lg.empty{background:#fff}
.hint{font-size:13px;color:var(--muted);margin:8px 0 14px}
.vi-cal{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:6px}
.vi-cal.loading{opacity:.5;pointer-events:none}
.vi-dow{text-align:center;font-size:12px;font-weight:600;color:var(--muted);padding:4px 0}
.vi-cell{min-height:78px;border:1.5px solid var(--line);border-radius:10px;background:#fff;padding:7px 8px;display:flex;flex-direction:column;gap:2px;text-align:left;font-family:'Outfit',sans-serif;cursor:pointer;color:var(--ink)}
.vi-cell.blank{border:0;background:none;cursor:default}
.vi-cell:hover:not(:disabled):not(.blank){border-color:var(--accent)}
.vi-cell.open{background:#f0fdf4;border-color:#bbf7d0}
.vi-cell.sold{background:#fef2f2;border-color:#fecaca}
.vi-cell.stop{background:#f1f5f9;border-color:#cbd5e1}
.vi-cell.sel{box-shadow:inset 0 0 0 2px var(--accent);background:var(--soft)}
.vi-cell.edge{background:#bae6fd}
.vi-cell.today .vi-day{background:var(--accent);color:#fff;border-radius:999px;padding:0 7px}
.vi-cell.past{opacity:.4;cursor:not-allowed}
.vi-day{font-weight:600;font-size:13px;align-self:flex-start}
.vi-units{font-size:11px;font-weight:600;color:#166534}
.vi-cell.sold .vi-units{color:#b91c1c}
.vi-cell.stop .vi-units{color:#475569}
.vi-units.muted{color:#94a3b8;font-weight:500}
.vi-price{font-size:12px;color:#0c4a6e;font-weight:600}
.cal-actions{display:flex;gap:8px;margin-top:14px;flex-wrap:wrap}
.vi-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px;margin-top:6px}
.vi-grid.four{grid-template-columns:repeat(4,1fr)}
.chips{display:flex;flex-wrap:wrap;gap:8px}
.chip{padding:7px 14px;border:1.5px solid var(--line);border-radius:999px;background:#fff;font:500 13px 'Outfit',sans-serif;color:#475569;cursor:pointer}
.chip:hover{border-color:var(--accent);color:var(--accent)}
.chip.on{background:var(--accent);border-color:var(--accent);color:#fff}
.check{display:flex;align-items:center;gap:10px;padding:12px 14px;border:1.5px solid var(--line);border-radius:10px;font-size:14px;cursor:pointer;align-self:end;margin-bottom:14px}
.check.on{background:#f0f9ff;border-color:#7dd3fc}
.check input{width:16px;height:16px;accent-color:var(--accent)}
.vi-foot{display:flex;justify-content:flex-end;align-items:center;gap:10px;flex-wrap:wrap;margin-top:10px;padding-top:16px;border-top:1px solid var(--soft)}
.vi-error{background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;border-radius:8px;padding:10px 14px;font-size:14px;margin:12px 0;display:flex;gap:10px;align-items:center;flex-wrap:wrap}
.vi-state{background:#fff;border:1px dashed #bcd7ea;border-radius:14px;padding:60px 20px;text-align:center;color:var(--muted);display:flex;flex-direction:column;align-items:center;gap:14px}
.vi-state p{margin:0}
.empty-ico{width:60px;height:60px;border-radius:50%;background:var(--soft);color:var(--accent);display:grid;place-items:center;font-size:26px}
.spinner{width:38px;height:38px;border:3px solid #bae6fd;border-top-color:var(--accent);border-radius:50%;animation:vi-spin 1s linear infinite}
@keyframes vi-spin{to{transform:rotate(360deg)}}
.toast{position:fixed;top:20px;right:20px;z-index:2000;padding:12px 18px;border-radius:10px;font-size:14px;font-weight:500;color:#fff;background:var(--green);box-shadow:0 6px 24px rgba(15,23,42,.2)}
.toast.err{background:var(--danger)}
@media (max-width:760px){
  .vi-head{flex-direction:column;align-items:stretch}
  .vi .vi-head .btn.vi-add{width:100%!important}
  .vi{padding:20px 12px}
  .vi-card{padding:16px 14px}
  .table-card{padding:0}
  .vi-grid,.vi-grid.four{grid-template-columns:1fr 1fr}
  .vi-cal{gap:3px}
  .vi-cell{min-height:64px;padding:4px}
  .vi-price{display:none}
  .vi-units{font-size:10px}
}
@media (max-width:480px){.vi-grid,.vi-grid.four{grid-template-columns:1fr}}
@media (prefers-reduced-motion:reduce){.spinner{animation-duration:3s}}
`;